// src/components/DateRangeControl.tsx - BULLETPROOF VERSION
import React, { useState, useCallback, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import type { DateRangeFilter, ExistenceFilter } from '../types';
import "react-datepicker/dist/react-datepicker.css";

interface DateRangeControlProps {
  value: DateRangeFilter | ExistenceFilter | null;
  onChange: (value: DateRangeFilter | ExistenceFilter | null) => void;
  fieldName?: string;
}

// ✅ BULLETPROOF: Persistent state that preserves user selections
interface PersistentState {
  mode: 'before' | 'after' | 'between';
  startDate: Date | null;
  endDate: Date | null;
  existenceState: 'none' | 'has_data' | 'is_empty';
}

const DateRangeControl: React.FC<DateRangeControlProps> = ({
  value,
  onChange,
  fieldName = 'unknown'
}) => {
  // ✅ BULLETPROOF: Separate persistent state from sync state
  const [persistentState, setPersistentState] = useState<PersistentState>({
    mode: 'between',
    startDate: null,
    endDate: null,
    existenceState: 'none'
  });

  // ✅ BULLETPROOF: Track initialization and internal updates
  const isInitializedRef = useRef(false);
  const internalUpdateRef = useRef(false);
  const lastExternalValueRef = useRef<string>('');
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentValueString = JSON.stringify(value);

  // ✅ BULLETPROOF: Sync external changes WITHOUT losing user selections
  useEffect(() => {
    // Skip if this is an internal update
    if (internalUpdateRef.current) {
      internalUpdateRef.current = false;
      return;
    }

    // Skip if value hasn't actually changed
    if (currentValueString === lastExternalValueRef.current) {
      return;
    }


    lastExternalValueRef.current = currentValueString;

    if (value) {
      if (value.type === 'existence') {
        // Only update existence state, preserve mode and dates
        setPersistentState(prev => ({
          ...prev,
          existenceState: value.has_value ? 'has_data' : 'is_empty'
        }));
      } else if (value.type === 'date_range') {
        setPersistentState(prev => ({
          ...prev,
          mode: value.mode || prev.mode, // Preserve mode if not specified
          startDate: value.start_date ? new Date(value.start_date) : null,
          endDate: value.end_date ? new Date(value.end_date) : null,
          existenceState: 'none'
        }));
      }
    } else if (!isInitializedRef.current) {
      // Only reset if not initialized, otherwise preserve user's selections
      setPersistentState(prev => ({
        ...prev,
        startDate: null,
        endDate: null,
        existenceState: 'none'
      }));
    }

    isInitializedRef.current = true;
  }, [currentValueString, fieldName, value]);

  // ✅ BULLETPROOF: Update external filter with internal update flag
  const updateExternalFilter = useCallback((newFilter: DateRangeFilter | ExistenceFilter | null) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {

      
      // Mark as internal update
      internalUpdateRef.current = true;
      lastExternalValueRef.current = JSON.stringify(newFilter);
      
      onChange(newFilter);
    }, 150);
  }, [fieldName, onChange]);

  // ✅ BULLETPROOF: Mode switching that preserves dates
  const handleModeChange = useCallback((newMode: 'before' | 'after' | 'between') => {
    if (newMode === persistentState.mode) return;
    

    
    setPersistentState(prev => {
      let newStartDate = prev.startDate;
      let newEndDate = prev.endDate;
      
      // Adjust dates based on new mode
      if (newMode === 'before') {
        newStartDate = null; // Clear start for 'before' mode
      } else if (newMode === 'after') {
        newEndDate = null; // Clear end for 'after' mode
      }
      
      return {
        ...prev,
        mode: newMode,
        startDate: newStartDate,
        endDate: newEndDate,
        existenceState: 'none' // Clear existence when changing date mode
      };
    });
    
    // Build and apply filter with new mode
    const filter: DateRangeFilter = { type: 'date_range', mode: newMode };
    
    if (newMode === 'before' && persistentState.endDate) {
      filter.end_date = persistentState.endDate.toISOString().split('T')[0];
    } else if (newMode === 'after' && persistentState.startDate) {
      filter.start_date = persistentState.startDate.toISOString().split('T')[0];
    } else if (newMode === 'between') {
      if (persistentState.startDate) filter.start_date = persistentState.startDate.toISOString().split('T')[0];
      if (persistentState.endDate) filter.end_date = persistentState.endDate.toISOString().split('T')[0];
    }
    
    updateExternalFilter(filter.start_date || filter.end_date ? filter : null);
  }, [persistentState, fieldName, updateExternalFilter]);

  // ✅ BULLETPROOF: Date change handling
  const handleDateChange = useCallback((type: 'start' | 'end', date: Date | null) => {

    
    setPersistentState(prev => ({
      ...prev,
      [type === 'start' ? 'startDate' : 'endDate']: date,
      existenceState: 'none' // Clear existence when setting dates
    }));
    
    // Build filter based on current mode
    const currentState = { ...persistentState, [type === 'start' ? 'startDate' : 'endDate']: date };
    const filter: DateRangeFilter = { type: 'date_range', mode: currentState.mode };
    
    if (currentState.mode === 'before' && currentState.endDate) {
      filter.end_date = currentState.endDate.toISOString().split('T')[0];
    } else if (currentState.mode === 'after' && currentState.startDate) {
      filter.start_date = currentState.startDate.toISOString().split('T')[0];
    } else if (currentState.mode === 'between') {
      if (currentState.startDate) filter.start_date = currentState.startDate.toISOString().split('T')[0];
      if (currentState.endDate) filter.end_date = currentState.endDate.toISOString().split('T')[0];
    }
    
    updateExternalFilter(filter.start_date || filter.end_date ? filter : null);
  }, [persistentState, fieldName, updateExternalFilter]);

  // ✅ BULLETPROOF: Existence toggle
  const handleExistenceToggle = useCallback((newState: 'has_data' | 'is_empty') => {
    const finalState = persistentState.existenceState === newState ? 'none' : newState;
    
    setPersistentState(prev => ({
      ...prev,
      existenceState: finalState
    }));
    
    if (finalState !== 'none') {
      const existenceFilter: ExistenceFilter = {
        type: 'existence',
        has_value: finalState === 'has_data'
      };
      updateExternalFilter(existenceFilter);
    } else {
      // Restore date filter if dates exist
      const filter: DateRangeFilter = { type: 'date_range', mode: persistentState.mode };
      
      if (persistentState.mode === 'before' && persistentState.endDate) {
        filter.end_date = persistentState.endDate.toISOString().split('T')[0];
      } else if (persistentState.mode === 'after' && persistentState.startDate) {
        filter.start_date = persistentState.startDate.toISOString().split('T')[0];
      } else if (persistentState.mode === 'between') {
        if (persistentState.startDate) filter.start_date = persistentState.startDate.toISOString().split('T')[0];
        if (persistentState.endDate) filter.end_date = persistentState.endDate.toISOString().split('T')[0];
      }
      
      updateExternalFilter(filter.start_date || filter.end_date ? filter : null);
    }
  }, [persistentState, updateExternalFilter]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="premium-date-range">
      <div className={`date-controls ${persistentState.existenceState !== 'none' ? 'existence-mode' : ''}`}>
        <div className="date-mode-selector">
          <button
            type="button"
            className={`date-mode-button ${persistentState.mode === 'before' ? 'active' : ''}`}
            onClick={() => handleModeChange('before')}
          >
            Before
          </button>
          <button
            type="button"
            className={`date-mode-button ${persistentState.mode === 'after' ? 'active' : ''}`}
            onClick={() => handleModeChange('after')}
          >
            After
          </button>
          <button
            type="button"
            className={`date-mode-button ${persistentState.mode === 'between' ? 'active' : ''}`}
            onClick={() => handleModeChange('between')}
          >
            Between
          </button>
        </div>

        <div className="date-inputs-container">
          {(persistentState.mode === 'after' || persistentState.mode === 'between') && (
            <div className="date-input-group">
              <label className="date-input-label">
                {persistentState.mode === 'between' ? 'From Date' : 'After Date'}
              </label>
              <DatePicker
                selected={persistentState.startDate}
                onChange={(date) => handleDateChange('start', date)}
                placeholderText="Select date"
                className="premium-date-input"
                dateFormat="MMM d, yyyy"
                maxDate={persistentState.mode === 'between' ? persistentState.endDate || undefined : undefined}
                showPopperArrow={false}
                popperPlacement="bottom-start"
              />
            </div>
          )}

          {(persistentState.mode === 'before' || persistentState.mode === 'between') && (
            <div className="date-input-group">
              <label className="date-input-label">
                {persistentState.mode === 'between' ? 'To Date' : 'Before Date'}
              </label>
              <DatePicker
                selected={persistentState.endDate}
                onChange={(date) => handleDateChange('end', date)}
                placeholderText="Select date"
                className="premium-date-input"
                dateFormat="MMM d, yyyy"
                minDate={persistentState.mode === 'between' ? persistentState.startDate || undefined : undefined}
                showPopperArrow={false}
                popperPlacement="bottom-start"
              />
            </div>
          )}
        </div>
      </div>

      <div className="existence-toggle-buttons">
        <button
          type="button"
          className={`existence-toggle-btn ${persistentState.existenceState === 'has_data' ? 'active' : ''}`}
          onClick={() => handleExistenceToggle('has_data')}
        >
          Has Data
        </button>
        <button
          type="button"
          className={`existence-toggle-btn ${persistentState.existenceState === 'is_empty' ? 'active' : ''}`}
          onClick={() => handleExistenceToggle('is_empty')}
        >
          Is Empty
        </button>
      </div>
    </div>
  );
};

export default DateRangeControl;